import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import BrandMark from "@/components/BrandMark";
import { BRAND } from "@/lib/brand";

const NAV = [
    { href: "/#how", label: "How it works" },
    { href: "/#features", label: "Features" },
    { href: "/#integrations", label: "Integrations" },
    { href: "/#pricing", label: "Pricing" },
    { href: "/#faq", label: "FAQ" },
];

function scrollToHash(hash) {
    if (!hash) return;
    const el = document.getElementById(hash.replace("#", ""));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function SiteLayout({ children }) {
    const [open, setOpen] = useState(false);
    const loc = useLocation();

    useEffect(() => {
        setOpen(false);
        if (loc.hash) setTimeout(() => scrollToHash(loc.hash), 50);
        else window.scrollTo(0, 0);
    }, [loc.pathname, loc.hash]);

    return (
        <div className="min-h-screen bg-[#090d16] text-slate-300 grain" data-testid="public-site">
            <header className="sticky top-0 z-40 border-b border-white/5 bg-[#090d16]/85 backdrop-blur-xl">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
                    <Link to="/" aria-label={`${BRAND.name} home`}>
                        <BrandMark subtitle={null} />
                    </Link>
                    <nav className="hidden md:flex items-center gap-6 text-sm">
                        {NAV.map((n) => (
                            <Link
                                key={n.href}
                                to={n.href}
                                className="text-slate-400 hover:text-slate-100 transition-colors"
                            >
                                {n.label}
                            </Link>
                        ))}
                    </nav>
                    <div className="hidden md:flex items-center gap-2">
                        <Link
                            to="/login"
                            data-testid="site-signin"
                            className="text-sm font-medium text-slate-300 hover:text-white px-3 py-2"
                        >
                            Sign in
                        </Link>
                        <Link
                            to="/register"
                            data-testid="site-get-started"
                            className="text-sm font-semibold text-white bg-indigo-500 hover:bg-indigo-400 rounded-xl px-4 py-2 transition-colors"
                        >
                            Get started
                        </Link>
                    </div>
                    <button
                        className="md:hidden h-10 w-10 -mr-2 flex items-center justify-center text-slate-300"
                        onClick={() => setOpen((v) => !v)}
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                        data-testid="site-menu-toggle"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
                {open && (
                    <div className="md:hidden border-t border-white/5 px-4 pb-4 pt-2 space-y-1">
                        {NAV.map((n) => (
                            <Link
                                key={n.href}
                                to={n.href}
                                className="block rounded-lg px-3 py-2.5 text-sm text-slate-300 hover:bg-white/5"
                            >
                                {n.label}
                            </Link>
                        ))}
                        <div className="pt-2 grid grid-cols-2 gap-2">
                            <Link
                                to="/login"
                                className="text-center text-sm font-medium text-slate-200 border border-white/10 rounded-xl px-4 py-2.5"
                            >
                                Sign in
                            </Link>
                            <Link
                                to="/register"
                                className="text-center text-sm font-semibold text-white bg-indigo-500 rounded-xl px-4 py-2.5"
                            >
                                Get started
                            </Link>
                        </div>
                    </div>
                )}
            </header>

            <main>{children}</main>

            <footer className="border-t border-white/5 mt-24">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="lg:col-span-2 max-w-sm">
                        <BrandMark />
                        <p className="mt-4 text-sm text-slate-400 leading-relaxed">
                            An AI marketing manager for local businesses — find, check and fix
                            how you look online, and answer customers faster.
                        </p>
                        <p className="mt-4 text-xs text-slate-500">
                            A product of {BRAND.company}, {BRAND.city}.
                        </p>
                    </div>
                    <div>
                        <div className="label-eyebrow mb-3">Product</div>
                        <ul className="space-y-2 text-sm">
                            {NAV.slice(0, 4).map((n) => (
                                <li key={n.href}>
                                    <Link to={n.href} className="text-slate-400 hover:text-slate-100">
                                        {n.label}
                                    </Link>
                                </li>
                            ))}
                            <li>
                                <Link to="/login" className="text-slate-400 hover:text-slate-100">
                                    Sign in
                                </Link>
                            </li>
                        </ul>
                    </div>
                    <div>
                        <div className="label-eyebrow mb-3">Company</div>
                        <ul className="space-y-2 text-sm">
                            <li>
                                <a href={`mailto:${BRAND.email}`} className="text-slate-400 hover:text-slate-100">
                                    {BRAND.email}
                                </a>
                            </li>
                            <li>
                                <Link to="/privacy" className="text-slate-400 hover:text-slate-100">
                                    Privacy policy
                                </Link>
                            </li>
                            <li>
                                <Link to="/terms" className="text-slate-400 hover:text-slate-100">
                                    Terms of service
                                </Link>
                            </li>
                            <li>
                                <Link to="/data-deletion" className="text-slate-400 hover:text-slate-100">
                                    Data deletion
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>
                <div className="border-t border-white/5">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 text-xs text-slate-600 flex flex-col sm:flex-row gap-2 justify-between">
                        <span>© {new Date().getFullYear()} {BRAND.company}. All rights reserved.</span>
                        <span>Instagram, WhatsApp, Facebook and Google are trademarks of their owners.</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}
